import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import useFinishFlow, { FLOWS } from 'utilities/navigation/flow'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import Header from 'components/headers/withGreenBack'
import DotsLoading from 'components/loadings/dotsLoading'
import { SectionError } from 'components/states'
import { faPlain, toman } from 'utilities/helper/format'
import { createManualCashout, initManualCashout } from 'services/cashbox'
import {
    Card,
    Field,
    MoneyInput,
    Notice,
    PrimaryButton,
    apiMessage,
    inputClass,
} from 'modules/cashbox/ui'
import { CASHBOX_KEY, useCashbox } from 'modules/cashbox/hooks'

/**
 * پرداخت دستی وام — the manager giving the loan to a chosen member without a
 * draw.
 *
 * This is not just a transfer: `create_manual_cashout` also calls
 * `create_winner`, so the chosen member burns one of their shares exactly as
 * if they had won. That is why the screen says so plainly, and why the member
 * list here is the drawable one (`manual-cashout-init` already filters to
 * memberships with an unwon share).
 *
 * Only available when «پرداخت دستی وام» is enabled in settings — the endpoint
 * refuses otherwise.
 */
export default function CashboxManualCashoutPage() {
    const { id } = useParams()
    const finishFlow = useFinishFlow(FLOWS.cashboxManualCashout)
    const queryClient = useQueryClient()
    const { cashbox, isLoading: boxLoading } = useCashbox(id)

    const { data, isLoading, isError, refetch } = useQuery(
        ['cashbox-cashout-init', id],
        () => initManualCashout(id!),
        { enabled: Boolean(id), retry: false }
    )

    const [membershipId, setMembershipId] = useState<number | ''>('')
    const [bankAccountId, setBankAccountId] = useState<number | ''>('')
    const [amount, setAmount] = useState(0)
    const [newCycle, setNewCycle] = useState(true)

    const memberships = data?.memberships ?? []
    const selectedMembership = memberships.find(
        (membership) => membership.id === membershipId
    )

    const accounts = useMemo(() => {
        if (!selectedMembership) return []
        return (data?.bank_accounts ?? []).filter(
            (account: any) => account.member === selectedMembership.member_id
        )
    }, [data, selectedMembership])

    const balance = cashbox?.balance ?? 0
    const problems: Array<string> = []
    if (!membershipId) problems.push('عضو دریافت‌کننده را انتخاب کنید')
    if (!bankAccountId) problems.push('حساب بانکی مقصد را انتخاب کنید')
    if (amount <= 0) problems.push('مبلغ را وارد کنید')
    // `create_manual_cashout` refuses when `5000 <= amount and amount > balance`.
    if (amount >= 5000 && amount > balance)
        problems.push(`مبلغ از موجودی صندوق (${toman(balance)}) بیشتر است`)

    const cashout = useMutation({
        mutationFn: () =>
            createManualCashout({
                id: id!,
                receiver_id: Number(membershipId),
                bank_account_id: Number(bankAccountId),
                amount,
                new_cycle: newCycle,
            }),
        onSuccess: () => {
            // A loan handed out without a draw — it also burns one of the
            // member's shares, so it is a draw substitute, not a transfer, and
            // worth telling apart from قرعه‌کشی in the funnel.
            posthog.capture('cashbox_manual_cashout_done', {
                cashbox_id: id,
                amount,
                new_cycle: newCycle,
            })
            toast.success('پرداخت ثبت شد')
            queryClient.invalidateQueries(CASHBOX_KEY(id))
            queryClient.invalidateQueries(['cashbox-winners', id])
            queryClient.invalidateQueries(['get-boxes'])
            finishFlow(`/cashbox/${id}`)
        },
        onError: (error: any) => {
            posthog.capture('cashbox_manual_cashout_failed', {
                cashbox_id: id,
                amount,
                new_cycle: newCycle,
                status: error?.response?.status,
            })
            toast.error(apiMessage(error))
        },
    })

    if (boxLoading || isLoading) return <DotsLoading />

    if (cashbox?.membership?.role !== 'own')
        return (
            <div className="bg-white min-h-screen">
                <Header title="پرداخت دستی وام" />
                <div className="p-4">
                    <Notice tone="warn">
                        فقط مدیر صندوق می‌تواند پرداخت دستی انجام دهد.
                    </Notice>
                </div>
            </div>
        )

    if (!cashbox?.manual_cashout)
        return (
            <div className="bg-white min-h-screen">
                <Header title="پرداخت دستی وام" />
                <div className="p-4">
                    <Notice tone="warn">
                        «پرداخت دستی وام» برای این صندوق فعال نیست. از تنظیمات
                        صندوق آن را روشن کنید.
                    </Notice>
                </div>
            </div>
        )

    if (isError)
        return (
            <div className="bg-white min-h-screen">
                <Header title="پرداخت دستی وام" />
                <div className="p-4">
                    <SectionError onRetry={() => refetch()} />
                </div>
            </div>
        )

    return (
        <div className="bg-[#FAFAFA] min-h-screen pb-8">
            <div className="bg-white">
                <Header title="پرداخت دستی وام" />
            </div>

            <div className="px-4 pt-3">
                <Notice tone="warn">
                    با این کار، وام بدون قرعه‌کشی به عضو انتخاب‌شده پرداخت
                    می‌شود و یکی از سهم‌های او «برنده‌شده» ثبت می‌گردد؛ یعنی
                    دیگر در قرعه‌کشی‌های بعدی با آن سهم شرکت نمی‌کند.
                </Notice>

                <Card className="mt-3">
                    <div className="flex justify-between items-baseline">
                        <span className="text-sm text-text-secondary">
                            موجودی صندوق
                        </span>
                        <span className="text-sm font-bold text-black">
                            {toman(balance)}
                        </span>
                    </div>
                </Card>

                <div className="mt-4">
                    <Field label="دریافت‌کننده">
                        <select
                            value={membershipId}
                            onChange={(event) => {
                                setMembershipId(Number(event.target.value))
                                setBankAccountId('')
                            }}
                            className={inputClass}
                        >
                            <option value="">انتخاب عضو…</option>
                            {memberships.map((membership) => (
                                <option
                                    key={membership.id}
                                    value={membership.id}
                                >
                                    {membership.name?.trim() ||
                                        membership.phone_number}{' '}
                                    ({faPlain.format(membership.non_won_shares)}{' '}
                                    سهم باقی‌مانده)
                                </option>
                            ))}
                        </select>
                    </Field>

                    <Field
                        label="حساب بانکی مقصد"
                        hint={
                            selectedMembership && accounts.length === 0
                                ? 'این عضو حساب بانکی تاییدشده‌ای ندارد'
                                : undefined
                        }
                    >
                        <select
                            value={bankAccountId}
                            disabled={!selectedMembership}
                            onChange={(event) =>
                                setBankAccountId(Number(event.target.value))
                            }
                            className={inputClass}
                        >
                            <option value="">انتخاب حساب…</option>
                            {accounts.map((account: any) => (
                                <option key={account.id} value={account.id}>
                                    {account.bank_name} · {account.owner_name}
                                </option>
                            ))}
                        </select>
                    </Field>

                    <Field
                        label="مبلغ"
                        hint={`مبلغ معمول هر وام: ${toman(
                            cashbox?.period?.loan_amount ?? 0
                        )}`}
                    >
                        <MoneyInput value={amount} onChange={setAmount} />
                    </Field>

                    <Card className="mb-4">
                        <label className="flex items-start gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={newCycle}
                                onChange={(event) =>
                                    setNewCycle(event.target.checked)
                                }
                                className="w-4 h-4 mt-0.5 accent-green-1"
                            />
                            <span className="text-sm text-black">
                                پس از پرداخت، نوبت بعدی شروع شود
                            </span>
                        </label>
                    </Card>

                    {problems.length > 0 && (
                        <Notice tone="warn">
                            {problems.map((problem) => (
                                <span key={problem}>
                                    • {problem}
                                    <br />
                                </span>
                            ))}
                        </Notice>
                    )}

                    <div className="mt-4">
                        <PrimaryButton
                            disabled={problems.length > 0 || cashout.isLoading}
                            onClick={() => {
                                if (
                                    window.confirm(
                                        `${toman(
                                            amount
                                        )} به این عضو پرداخت شود و یک سهم او برنده ثبت شود؟`
                                    )
                                )
                                    cashout.mutate()
                            }}
                        >
                            {cashout.isLoading ? 'در حال ثبت…' : 'پرداخت'}
                        </PrimaryButton>
                    </div>
                </div>
            </div>
        </div>
    )
}
